import { useEffect, useMemo, useState } from 'react'
import VIDEO_URLS from '../utils/videourls' // solo .mp4 para Safari

// Detecta iPad/iOS Safari (incluye iPadOS)
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
const isIPadSafari = isIOS && isSafari

// Warmup de medios para Safari/iPad: usa <video> oculto + seeks
async function warmOneSafari(url, onTick) {
  return new Promise(async (resolve) => {
    const v = document.createElement('video')
    v.src = url
    v.preload = 'auto'
    v.muted = true
    v.playsInline = true
    v.style.position = 'fixed'
    v.style.width = '1px'
    v.style.height = '1px'
    v.style.opacity = '0'
    v.style.pointerEvents = 'none'
    document.body.appendChild(v)

    let timeout
    const cleanup = () => {
      clearTimeout(timeout)
      try { v.pause() } catch {}
      v.remove()
      onTick?.(1)
      resolve()
    }

    // Avanza si está casi todo en buffer
    const isAlmostBuffered = () => {
      try {
        if (isNaN(v.duration) || !isFinite(v.duration) || v.duration === 0) return false
        if (v.buffered.length === 0) return false
        const end = v.buffered.end(v.buffered.length - 1)
        return end >= v.duration - 0.25 // 250ms del final
      } catch { return false }
    }

    v.addEventListener('loadedmetadata', async () => {
      try { await v.play().catch(() => {}) } catch {}
      // Secuencia de saltos para forzar rangos
      const points = [0.0, 0.25, 0.5, 0.75, 0.98]
      for (const p of points) {
        if (isAlmostBuffered()) break
        try { v.currentTime = Math.max(0, v.duration * p) } catch {}
        // Espera un poco a que llegue el rango
        await new Promise(r => setTimeout(r, 600))
      }
    }, { once: true })

    const tick = () => {
      if (isAlmostBuffered()) cleanup()
    }
    v.addEventListener('progress', tick)
    v.addEventListener('canplaythrough', tick)
    v.addEventListener('loadeddata', tick)
    v.addEventListener('ended', cleanup)

    // Corte de seguridad para no quedar colgado
    timeout = setTimeout(cleanup, 12000) // 12s por vídeo como tope
    // Inicia la descarga
    try { v.load() } catch {}
  })
}

// Orquesta en serie (Safari se lleva mal con mucha concurrencia)
async function warmupSafari(urls, onTick) {
  for (const u of urls) {
    await warmOneSafari(u, onTick)
  }
}

// --- Config ---
const STORAGE_KEY = 'ermx_warmup_status_v1'

// Hash simple de la lista para saber si cambió la “versión” de contenidos
function hashString(s) {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i)
  return (h >>> 0).toString(16)
}
const VERSION = hashString(VIDEO_URLS.join('|'))

// --- Descarga y drenado (materializa en caché) ---
async function fetchAndDrain(u, cacheMode = 'reload') {
  const r = await fetch(u, { cache: cacheMode, credentials: 'same-origin' })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  if (cacheMode === 'reload') {
    if (r.body && r.body.getReader) {
      const rd = r.body.getReader()
      while (true) {
        const { done } = await rd.read()
        if (done) break
      }
    } else {
      await r.blob()
    }
  }
}

async function warmup(urls, { cacheMode = 'reload', concurrency = 3, retries = 2, onTick } = {}) {
  let i = 0
  const failed = []
  const worker = async () => {
    while (i < urls.length) {
      const u = urls[i++]
      let ok = false, attempt = 0
      while (!ok && attempt <= retries) {
        try {
          await fetchAndDrain(u, cacheMode)
          ok = true
        } catch (e) {
          attempt++
          if (attempt > retries) failed.push(u)
          else await new Promise(r => setTimeout(r, 500 * attempt)) // backoff
        } finally {
          onTick?.(1)
        }
      }
    }
  }
  await Promise.all(Array.from({ length: concurrency }, worker))
  return { failed }
}

// --- Verificación A: Resource Timing (sirve en Safari). Requiere TAO si es cross-origin ---
async function verifyByTiming(urls) {
  // Asegura que caben todos los registros de timings
  if (performance.setResourceTimingBufferSize) {
    performance.setResourceTimingBufferSize(Math.max(300, urls.length * 3))
  }
  performance.clearResourceTimings()

  // “Toca” todos con force-cache para que se registren entradas
  await Promise.all(urls.map(u => fetch(u, { cache: 'force-cache' }).catch(() => {})))
  await new Promise(r => setTimeout(r, 0)) // deja registrar timings

  const hits = [], miss = [], unknown = []
  for (const u of urls) {
    // ⚠️ Performance guarda ABSOLUTAS
    const abs = new URL(u, window.location.href).href

    // 1) Busca por nombre exacto (absoluto)
    let entries = performance.getEntriesByName(abs)

    // 2) Fallback (Safari a veces guarda con otra forma): filtramos por sufijo
    if (!entries || entries.length === 0) {
      const all = performance.getEntriesByType('resource')
      entries = all.filter(e => {
        if (!e || typeof e.name !== 'string') return false
        // e.name podría ser absoluta; probamos match por sufijo del path
        try {
          const nameUrl = new URL(e.name, window.location.href)
          const target = new URL(abs)
          return nameUrl.pathname === target.pathname
        } catch { return false }
      })
    }

    const e = entries && entries.length ? entries[entries.length - 1] : null
    if (!e) {
      unknown.push(u) // sin entrada, sin TAO o no registrado
      continue
    }

    // Heurística de HIT: transferSize === 0 con cuerpo > 0 (sirve en Safari/Chrome)
    if (e.encodedBodySize > 0 && e.transferSize === 0) hits.push(u)
    else miss.push(u)
  }

  return { hits, miss, unknown }
}


// --- Verificación B: only-if-cached (solo same-origin; no Safari) ---
async function verifyHttpCache(urls) {
  const hits = [], miss = []
  for (const u of urls) {
    try {
      const res = await fetch(u, { cache: 'only-if-cached', mode: 'same-origin' })
      if (res.ok) hits.push(u)
      else miss.push(u)
    } catch {
      miss.push(u)
    }
  }
  return { hits, miss }
}

export default function WarmupPage() {
  const urls = useMemo(() => VIDEO_URLS.slice(), [])
  const total = urls.length

  const [done, setDone] = useState(0)
  const [failed, setFailed] = useState([])
  const [verified, setVerified] = useState(null) // {hits, miss, unknown?}
  const [verifying, setVerifying] = useState(false)

  // permite scroll aunque el body global tenga overflow hidden
  useEffect(() => {
    const prevBody = document.body.style.overflowY
    const prevHtml = document.documentElement.style.overflowY
    document.body.style.overflowY = 'auto'
    document.documentElement.style.overflowY = 'auto'
    return () => {
      document.body.style.overflowY = prevBody
      document.documentElement.style.overflowY = prevHtml
    }
  }, [])

useEffect(() => {
  let cancelled = false
  const params = new URLSearchParams(window.location.search)
  const force = params.get('force') === '1'

  const stored = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') } catch { return null }
  })()
  const alreadyWarm =
    !force && stored && stored.version === VERSION && stored.total === total && stored.ok

  const tick = (inc) => { if (!cancelled) setDone(d => d + inc) }

  ;(async () => {
    if (alreadyWarm) {
      setDone(total)
      const v = await doVerify(urls)
      if (!cancelled) setVerified(v)
      return
    }

    // 👇 AQUÍ está la diferencia clave
    if (isIPadSafari) {
      await warmupSafari(urls, tick)  // precarga en la media cache de Safari
    } else {
      await warmup(urls, { cacheMode: 'reload', concurrency: 3, retries: 2, onTick: tick })
    }

    if (cancelled) return
    const v = await doVerify(urls)
    setVerified(v)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: VERSION,
      total,
      ok: (v.hits?.length || 0) >= Math.floor(total * 0.9),
      finishedAt: new Date().toISOString()
    }))
  })()

  return () => { cancelled = true }
}, [urls, total])


  async function doVerify(urls) {
    // 1) Intenta Resource Timing
    const v = await verifyByTiming(urls)
    // Si hay muchos 'unknown', intenta fallback only-if-cached (igual dominio)
    const crossOriginOrNoTAO = v.unknown && v.unknown.length > 0
    if (crossOriginOrNoTAO) {
      const b = await verifyHttpCache(urls)
      return { hits: b.hits, miss: b.miss, unknown: v.unknown }
    }
    return v
  }

  const onManualVerify = async () => {
    setVerifying(true)
    setVerified(await doVerify(urls))
    setVerifying(false)
  }

  const retryFailed = async () => {
    if (!failed.length) return
    const { failed: f2 } = await warmup(failed, {
      cacheMode: 'reload',
      concurrency: 2,
      retries: 3,
      onTick: inc => setDone(d => d + inc)
    })
    setFailed(f2)
    setVerified(await doVerify(urls))
  }

  const forceWarm = async () => {
    setDone(0)
    setFailed([])
    const { failed } = await warmup(urls, {
      cacheMode: 'reload',
      concurrency: 3,
      retries: 2,
      onTick: inc => setDone(d => d + inc)
    })
    setFailed(failed)
    setVerified(await doVerify(urls))
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: VERSION,
      total,
      ok: failed.length === 0,
      finishedAt: new Date().toISOString()
    }))
  }

  const saveSeal = () => {
    const hits = verified?.hits?.length ?? null
    const miss = verified?.miss?.length ?? null
    const unknown = verified?.unknown?.length ?? null
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: VERSION, total,
      ok: hits !== null ? hits >= Math.floor(total * 0.9) : null,
      hits, miss, unknown,
      finishedAt: new Date().toISOString()
    }))
    alert('Marcado: warmup completado en este dispositivo.')
  }

  const unknownInfo = verified?.unknown?.length
    ? ` · desconocidos (TAO/CORS): ${verified.unknown.length}`
    : ''

  return (
    <div style={{ fontFamily:'system-ui', padding:24, lineHeight:1.45, maxWidth:1000 }}>
      <h1>Precargando vídeos…</h1>
      <p style={{ margin:'8px 0' }}>
        <strong>{done}</strong> / {total} completados
        {failed.length > 0 && <span> · Fallidos: {failed.length}</span>}
      </p>
      <progress max={total || 1} value={done} style={{ width:480, height:16 }} />
      {done >= total && <p style={{ marginTop:10 }}>✓ Descarga completada.</p>}

      <div style={{ marginTop:16, display:'flex', flexWrap:'wrap', gap:8 }}>
        <button onClick={onManualVerify} disabled={verifying} style={{ padding:'8px 12px', cursor:'pointer' }}>
          {verifying ? 'Verificando…' : 'Verificar caché'}
        </button>
        <button onClick={retryFailed} disabled={!failed.length} style={{ padding:'8px 12px', cursor: failed.length ? 'pointer':'not-allowed' }}>
          Reintentar fallos ({failed.length})
        </button>
        <button onClick={forceWarm} style={{ padding:'8px 12px', cursor:'pointer' }}>
          Forzar recarga (red)
        </button>
        <button onClick={saveSeal} style={{ padding:'8px 12px', cursor:'pointer' }}>
          Guardar sello local
        </button>
      </div>

      {verified && (
        <div style={{ marginTop:18 }}>
          <h3>Resultado de verificación</h3>
          <p>
            HIT: {verified.hits?.length || 0}
            {' · '}MISS: {verified.miss?.length || 0}
            {unknownInfo}
          </p>

          {verified.hits?.length ? (
            <details open>
              <summary>HIT (en caché)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.hits.map(u => <li key={u}>✅ {u.split('/').pop()}</li>)}
                </ul>
              </div>
            </details>
          ) : null}

          {verified.miss?.length ? (
            <details open style={{ marginTop:8 }}>
              <summary>MISS (no encontrado en caché)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.miss.map(u => <li key={u}>❌ {u.split('/').pop()}</li>)}
                </ul>
              </div>
            </details>
          ) : null}

          {verified.unknown?.length ? (
            <details style={{ marginTop:8 }}>
              <summary>UNKNOWN (sin datos de timing — añade TAO)</summary>
              <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
                <ul style={{ columns: 2, marginTop:8 }}>
                  {verified.unknown.map(u => <li key={u}>❓ {u.split('/').pop()}</li>)}
                </ul>
              </div>
              <p style={{marginTop:8}}>
                Para que estos pasen a HIT/MISS, sirve los vídeos con
                <code> Timing-Allow-Origin: *</code>.
              </p>
            </details>
          ) : null}
        </div>
      )}

      <details style={{ marginTop:18 }}>
        <summary>Ver archivos (lista completa)</summary>
        <div style={{ maxHeight:'60vh', overflowY:'auto', WebkitOverflowScrolling:'touch', paddingRight:8 }}>
          <ul style={{ columns: 2, marginTop:8 }}>
            {urls.map(u => <li key={u}>{u.split('/').pop()}</li>)}
          </ul>
        </div>
      </details>

      {/* Test real: descomenta y prueba en Offline */}
      {/* <video src={urls[0]} controls width="480" style={{marginTop:16}} /> */}
    </div>
  )
}
